import React, { useState } from "react";

import { Collapse } from "@mui/material";
import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { pluralize } from "@app/common/utils";
import { formatNumber } from "@app/utils/utils";

import Stack, { Row } from "@rui/Stack";
import TextField from "@rui/TextField";

export const MINIMUM_NOTICE_BUFFER = {
  DEFAULT: 4,
  MAX: 10000, // something really large, but not Infinity
  MIN: 0,
};

const MINIMUM_NOTICE_BUFFER_ERROR = `Minimum notice must be between ${MINIMUM_NOTICE_BUFFER.MIN} and ${formatNumber(MINIMUM_NOTICE_BUFFER.MAX)} hours`;

interface MinimumNoticeInputProps {
  onBlur?: () => void;
  onChange: (newValue: number) => void;
  onFocus?: () => void;
  value: number;
}

const NumberInput = styled(TextField)(({ theme }) => ({
  width: "50px",
  minWidth: 0,

  "& .MuiInputBase-input": {
    textAlign: "center",
    fontSize: "0.875rem",
    padding: theme.spacing(0.75, 0.5),

    /* Hide number input arrows in Chrome, Safari, Edge, Opera */
    "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0,
    },
    /* ...and in Firefox */
    MozAppearance: "textfield",
  },
}));

const MinimumNoticeInput = ({
  onBlur,
  onChange,
  onFocus,
  value,
}: MinimumNoticeInputProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const inputValue = parseInt(event.target.value ?? "0");

    if (inputValue > MINIMUM_NOTICE_BUFFER.MAX) {
      setErrorMessage(MINIMUM_NOTICE_BUFFER_ERROR);
    } else if (errorMessage) {
      setErrorMessage(null);
    }

    onChange(inputValue);
  };

  return (
    <Stack>
      <Row spacing={1} variant="left">
        <NumberInput
          onBlur={onBlur}
          onChange={handleChange}
          onFocus={onFocus}
          type="number"
          inputProps={{
            min: MINIMUM_NOTICE_BUFFER.MIN,
            max: MINIMUM_NOTICE_BUFFER.MAX,
          }}
          value={value}
          error={Boolean(errorMessage)}
        />
        <Typography variant="bodySecondaryReg" color="shades.neutral.800">
          {pluralize("hour", value)} before event start time
        </Typography>
      </Row>
      <Collapse in={Boolean(errorMessage)} unmountOnExit>
        <Typography variant="labelReg" color="error.main">
          {errorMessage}
        </Typography>
      </Collapse>
    </Stack>
  );
};

export default MinimumNoticeInput;
